import { CommonModule, NgIf } from '@angular/common';
import { Component, OnDestroy, OnInit, ViewEncapsulation } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ActivatedRoute, Router, RouterLink, RouterOutlet } from '@angular/router';
import { FuseFullscreenComponent } from '@fuse/components/fullscreen';
import { FuseLoadingBarComponent } from '@fuse/components/loading-bar';
import {
    FuseNavigationService,
    FuseVerticalNavigationComponent,
} from '@fuse/components/navigation';
import { FuseMediaWatcherService } from '@fuse/services/media-watcher';
import { AuthService } from 'app/core/auth/auth.service';
import { NavigationService } from 'app/core/navigation/navigation.service';
import { Navigation } from 'app/core/navigation/navigation.types';
import { User } from 'app/core/user/user.types';
import { LanguagesComponent } from 'app/layout/common/languages/languages.component';
import { NotificationsComponent } from 'app/layout/common/notifications/notifications.component';
import { SchemeToggleComponent } from 'app/layout/common/scheme-toggle/scheme-toggle.component';
import { UserComponent } from 'app/layout/common/user/user.component';
import { UserNotesService } from 'app/core/user-notes/user-notes.service';
import { InfosDialogComponent } from 'app/modules/user-notes/infos-dialog/infos-dialog.component';
import { Observable, Subject, take, takeUntil } from 'rxjs';

@Component({
    selector: 'compact-layout',
    templateUrl: './compact.component.html',
    encapsulation: ViewEncapsulation.None,
    standalone: true,
    imports: [
        FuseLoadingBarComponent,
        MatButtonModule,
        MatIconModule,
        MatDialogModule,
        MatTooltipModule,
        LanguagesComponent,
        FuseFullscreenComponent,
        NotificationsComponent,
        SchemeToggleComponent,
        UserComponent,
        NgIf,
        RouterOutlet,
        FuseVerticalNavigationComponent,
        CommonModule,
        RouterLink
    ],
})
export class CompactLayoutComponent implements OnInit, OnDestroy {
    isScreenSmall: boolean = false;
    navigation$?: Observable<Navigation>;
    private _unsubscribeAll: Subject<any> = new Subject<any>();
    lightMode: boolean = false;
    user$?: Observable<User | null>;
    idUser: string | undefined;
    currentUser: User | undefined;
    hasActiveNotes$: Observable<boolean>;
    /**
     * Constructor
     */
    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _navigationService: NavigationService,
        private _fuseMediaWatcherService: FuseMediaWatcherService,
        private _fuseNavigationService: FuseNavigationService,
        private _authService: AuthService,
        private _userNotesService: UserNotesService,
        private _matDialog: MatDialog,
    ) {
        this.hasActiveNotes$ = this._userNotesService.hasActiveNotes$;
        this.user$ = this._authService.user$;
        this.user$.subscribe((user: User | null) => {
            if (user) {
                this.idUser = user._id;
                this.currentUser = user;
            }
        });
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for current year
     */
    get currentYear(): number {
        return new Date().getFullYear();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Lifecycle hooks
    // -----------------------------------------------------------------------------------------------------

    /**
     * On init
     */
    ngOnInit(): void {
        // Subscribe to navigation data
        this.navigation$ = this._navigationService.navigation$;

        // Subscribe to media changes
        this._fuseMediaWatcherService.onMediaChange$
            .pipe(takeUntil(this._unsubscribeAll))
            .subscribe(({ matchingAliases }) => {
                // Check if the screen is small
                this.isScreenSmall = !matchingAliases.includes('md');
            });
    }

    /**
     * On destroy
     */
    ngOnDestroy(): void {
        // Unsubscribe from all subscriptions
        this._unsubscribeAll.next(null);
        this._unsubscribeAll.complete();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Toggle navigation
     *
     * @param name
     */
    toggleNavigation(name: string): void {
        // Get the navigation
        const navigation =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                name
            );

        if (navigation) {
            // Toggle the opened status
            navigation.toggle();
        }
    }

    /**
     * Ouvre la modale d'informations listant les parties actives.
     */
    openInfosDialog(): void {
        this._userNotesService.notes$
            .pipe(take(1))
            .subscribe((notes) => {
                this._matDialog.open(InfosDialogComponent, {
                    width: '99%',
                    maxWidth: '800px',
                    data: { sections: notes?.sections ?? [] },
                });
            });
    }
}
